import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../../../../shared/modal';

type FileItem = { name: string; url: string };

const isVideo = (url: string) => {
    if(typeof url !==  'string') return false;
    return /\.(mp4|webm|ogg|mov|m4v)$/i.test(url?.split('?')[0] || '');
}

const isImage = (url: string) => {
    if (typeof url !== 'string') return false;
    return /\.(jpg|jpeg|png|gif|webp|avif|bmp|svg)$/i.test(url?.split('?')[0] || '');

}

interface MediaSliderProps {
    files: FileItem[];
    openModal: boolean;
    handleClose: () => void;
    startIndex?: number;
    onIndexChange?: (i: number) => void;
    className?: string;
}

const MediaFeedback: React.FC<MediaSliderProps> = ({
    files,
    openModal,
    handleClose,
    startIndex = 0,
    onIndexChange,
    className = '',
}) => {
    const [index, setIndex] = useState(Math.min(Math.max(startIndex, 0), Math.max(files.length - 1, 0)));
    const trackRef = useRef<HTMLDivElement>(null);
    const pointer = useRef<{ x: number; start: number } | null>(null);

    useEffect(() => {
        onIndexChange?.(index);
    }, [index, onIndexChange]);

    const go = useCallback(
        (dir: number) => {
            if (!files?.length) return;
            setIndex((i) => {
                const next = (i + dir + files.length) % files.length;
                return next;
            });
        },
        [files?.length]
    );

    // keyboard navigation
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') go(1);
            if (e.key === 'ArrowLeft') go(-1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [go]);

    const onPointerDown = (e: React.PointerEvent) => {
        pointer.current = { x: e.clientX, start: e.clientX };
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (!pointer.current || !trackRef.current) return;
        pointer.current.x = e.clientX;
        const dx = pointer.current.x - pointer.current.start;
        trackRef.current.style.transform = `translateX(calc(${-index * 100}% + ${dx}px))`;
    };

    const onPointerUp = (e: React.PointerEvent) => {
        if (!pointer.current || !trackRef.current) return;
        const dx = pointer.current.x - pointer.current.start;
        trackRef.current.style.transform = `translateX(${-index * 100}%)`;
        if (Math.abs(dx) > 60) {
            go(dx < 0 ? 1 : -1);
        }
        pointer.current = null;
    };

    const slides = useMemo(() => files ?? [], [files]);

    if (!slides.length) {
        return (
            <div className={`flex h-full min-h-[420px] items-center justify-center rounded-xl border border-dashed ${className}`}>
                <p className='text-sm text-gray-500'>No files</p>
            </div>
        );
    }

    return (
        <Modal
            open={openModal}
            title='Export Data - Trips'
            headerStyle='border-b-1 py-3 mb-4'
            onClose={handleClose}
            contentContainerStyle='!rounded-xl min-w-[95%] md:min-w-[70%] lg:min-w-[60%]'
        >
            <div className='p-2 max-w-[95vw] md:min-w-[70%] lg:min-w-[60%] min-h-[500px]'>
                <div className={`relative w-full h-full ${className}`}>
                    <div
                        className='overflow-hidden rounded-xl bg-black/5 dark:bg-white/5 h-[420px] md:h-[520px]'
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={onPointerUp}
                        onPointerCancel={onPointerUp}
                    >
                        <div
                            ref={trackRef}
                            className='flex h-full transition-transform duration-300 ease-out'
                            style={{ transform: `translateX(${-index * 100}%)` }}
                        >
                            {slides.map((f, i) => (
                                <div key={i} className='min-w-full h-full flex items-center justify-center bg-black'>
                                    <div className='relative w-full h-full flex items-center justify-center'>
                                        {isImage(f?.url) && (
                                            <img
                                                src={f.url}
                                                alt={f.name || `image-${i}`}
                                                className='max-h-full w-full object-contain'
                                                loading='lazy'
                                            />
                                        )}

                                        {isVideo(f?.url) && (
                                            <video
                                                src={f?.url}
                                                className='max-h-full max-w-full'
                                                controls
                                                preload='metadata'
                                            />
                                        )}

                                        {!isImage(f.url) && !isVideo(f.url) && (
                                            <div className='text-center text-white px-4'>
                                                <p className='text-sm opacity-80'>Unsupported file type</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className='mt-2 flex items-center justify-between gap-2'>
                        <div className='truncate text-sm font-medium'>
                            {slides[index]?.name}
                        </div>
                        <div className='flex items-center gap-2'>
                            <a
                                href={slides[index]?.url}
                                download
                                className='text-xs px-2 py-1 rounded border hover:bg-black/5 dark:hover:bg-white/10'
                            >
                                Download
                            </a>
                            <span className='text-xs opacity-70'>
                                {index + 1}/{slides.length}
                            </span>
                        </div>
                    </div>

                    {slides.length > 1 && (
                        <>
                            <button
                                onClick={() => go(-1)}
                                className='absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 text-white px-3 py-2 hover:bg-black/70 focus:outline-none'
                                aria-label='Previous'
                            >
                                ‹
                            </button>
                            <button
                                onClick={() => go(1)}
                                className='absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 text-white px-3 py-2 hover:bg-black/70 focus:outline-none'
                                aria-label='Next'
                            >
                                ›
                            </button>
                        </>
                    )}

                    {slides.length > 1 && (
                        <div className='absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2'>
                            {slides.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setIndex(i)}
                                    className={`h-2 w-2 rounded-full transition ${i === index ? 'bg-white' : 'bg-white/50 hover:bg-white/70'
                                        }`}
                                    aria-label={`Go to slide ${i + 1}`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};
export default MediaFeedback;